import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, LocateFixed, MapPin } from "lucide-react";
import { addressAt, currentPosition, placeDetails, placeSuggestions } from "@/lib/maps";

const newToken = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export const AddressInput = ({ label, value, onChange, testId, placeholder, companyId, useMyLocation }) => {
  // Typing only touches local state — the parent (and its map, vehicle list and quote) is updated
  // once typing settles, so every keystroke stays smooth on a phone.
  const [text, setText] = useState(value?.address || "");
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [locating, setLocating] = useState(false);
  const token = useRef(newToken());
  const external = value?.address || "";

  useEffect(() => {
    // A value set from outside (saved-address chip, form reset) is already resolved — show it,
    // don't turn it into a new search.
    setText((t) => {
      if (!external || external === t) return t;
      setPicked(true);
      setSuggestions([]);
      return external;
    });
  }, [external]);

  useEffect(() => {
    if (picked) return undefined;
    const q = text.trim();
    const timer = setTimeout(async () => {
      if (q !== (value?.address || "")) {
        onChange({ ...value, address: q, formatted_address: q, place_id: null, lat: null, lng: null });
      }
      if (q.length < 3) {
        setSuggestions([]);
        return;
      }
      try {
        const results = await placeSuggestions(q, token.current, { companyId });
        setSuggestions(results);
        setLookupError(results.length ? "" : "No match — try adding the city and state.");
      } catch (e) {
        // Loud, not silent: the old canned list produced addresses with no coordinates, which
        // priced trips at zero miles.
        setSuggestions([]);
        setLookupError(e?.response?.status === 503
          ? "Address lookup is offline on this server — trip distance cannot be calculated. Tell your operator to add the Google Maps key."
          : "Address lookup failed. Check your connection and try again.");
      }
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, picked]);

  const pick = async (s) => {
    setPicked(true);
    setSuggestions([]);
    setLookupError("");
    setText(s.text);
    if (!s.place_id) {
      onChange({ address: s.text, formatted_address: s.text, place_id: null, lat: null, lng: null });
      return;
    }
    try {
      const place = await placeDetails(s.place_id);
      setText(place.address || s.text);
      onChange({
        address: place.address || s.text,
        formatted_address: place.formatted_address || s.text,
        place_id: place.place_id,
        lat: place.lat ?? null,
        lng: place.lng ?? null,
      });
      token.current = newToken();
    } catch {
      onChange({ address: s.text, formatted_address: s.text, place_id: null, lat: null, lng: null });
    }
  };

  const locate = async () => {
    setLocating(true);
    setLookupError("");
    try {
      const at = await currentPosition();
      const place = await addressAt(at);
      setPicked(true);
      setSuggestions([]);
      setText(place.address || "");
      onChange({
        address: place.address,
        formatted_address: place.formatted_address || place.address,
        place_id: place.place_id || null,
        lat: place.lat ?? at.lat,
        lng: place.lng ?? at.lng,
      });
      token.current = newToken();
    } catch (e) {
      setLookupError(e?.response?.data?.detail
        || e?.message
        || "Could not use your location. Type the address instead.");
    } finally {
      setLocating(false);
    }
  };

  return (
    <div className="relative">
      {label && <label className="mb-2 block text-xs uppercase tracking-widest text-gold font-mono">{label}</label>}
      <div className="relative">
        <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <Input
          data-testid={testId}
          value={text}
          placeholder={placeholder || "Start typing an address…"}
          autoComplete="off"
          onChange={(e) => { setPicked(false); setText(e.target.value); }}
          onBlur={() => setTimeout(() => setSuggestions([]), 200)}
          className="h-12 border-ink-600 bg-ink-800 pl-9 text-slate-100 placeholder:text-slate-500 focus-visible:ring-gold"
        />
      </div>
      {suggestions.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute z-[1200] mt-1 w-full overflow-hidden rounded-lg border border-ink-600 bg-ink-800 shadow-2xl"
        >
          {suggestions.map((s) => (
            <button
              type="button"
              key={s.place_id || s.text}
              data-testid={`${testId}-suggestion`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(s)}
              className="block w-full px-4 py-2.5 text-left text-sm text-slate-300 transition-colors hover:bg-gold/10 hover:text-gold"
            >
              <span className="block truncate">{s.primary || s.text}</span>
              {s.secondary && <span className="block truncate text-xs text-slate-500">{s.secondary}</span>}
            </button>
          ))}
        </div>
      )}
      {useMyLocation && (
        <button
          type="button"
          data-testid={`${testId}-locate`}
          onClick={locate}
          disabled={locating}
          className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-gold/80 transition-colors hover:text-gold disabled:cursor-not-allowed disabled:opacity-50"
        >
          {locating
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <LocateFixed className="h-3.5 w-3.5" />}
          {locating ? "Finding you…" : "Use my current location"}
        </button>
      )}
      {lookupError && suggestions.length === 0 && (
        <p data-testid={`${testId}-lookup-error`} className="mt-1.5 text-[11px] text-amber-400">
          {lookupError}
        </p>
      )}
      {value?.lat != null && (
        <p data-testid={`${testId}-pinned`} className="mt-1.5 font-mono text-[11px] text-emerald-400/80">
          Pinned — we can price this route exactly
        </p>
      )}
    </div>
  );
};
